// Scenariogenomlysning: läsbar rapport över tio projektprofiler och funna luckor.
import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import DocumentHeader from "@/components/BasProcess/DocumentHeader";
import {
  SCENARIOS,
  GAPS,
  EVENTS,
  COVERAGE_SUMMARY,
  DOCUMENT_COVERAGE,
  type Coverage,
} from "@/lib/basProcess/scenarios";
import { generateScenarioAuditWordDocument } from "@/lib/documentGenerator/scenarioAuditWordGenerator";
import { toast } from "sonner";
import { CheckCircle2, Download, FlaskConical } from "lucide-react";

interface Props {
  projectId?: string;
  projectName?: string;
}

const coverageVariant = (c: Coverage): "default" | "secondary" | "destructive" =>
  c === "klart" ? "default" : c === "delvis" ? "secondary" : "destructive";

const ScenarioAudit: React.FC<Props> = ({ projectName }) => {
  const [exporting, setExporting] = useState(false);
  const summary = useMemo(() => COVERAGE_SUMMARY(), []);
  const docCoverage = useMemo(() => DOCUMENT_COVERAGE(), []);
  const open = GAPS.filter((g) => g.status === "att bygga");

  const handleExport = async () => {
    setExporting(true);
    try {
      await generateScenarioAuditWordDocument(projectName);
      toast.success("Rapporten är exporterad till Word");
    } catch (e) {
      toast.error("Kunde inte skapa Word-filen");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <DocumentHeader
        docNumber="00c"
        title="Scenariogenomlysning"
        description="Tio projektprofiler ur åtta rollers perspektiv. Två referensprojekt körs från idéstadie till bruksskede och rör vid samtliga dokument."
        icon={FlaskConical}
        actions={
          <Button onClick={handleExport} disabled={exporting}>
            <Download className="mr-2 h-4 w-4" />
            {exporting ? "Skapar…" : "Exportera till Word"}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Täckning</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Progress value={summary.percent} />
          <p className="text-sm text-muted-foreground">
            {summary.klart} av {summary.total} rollperspektiv är helt täckta ({summary.percent} %).{" "}
            {summary.delvis} är delvis täckta och beskrivs som luckor nedan.
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge>{summary.klart} klart</Badge>
            <Badge variant="secondary">{summary.delvis} delvis</Badge>
            <Badge variant="outline">{open.length} luckor kvar att bygga</Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Projektprofiler</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {SCENARIOS.map((s) => (
              <AccordionItem key={s.id} value={s.id}>
                <AccordionTrigger>
                  <div className="flex flex-1 flex-wrap items-center gap-2 pr-4 text-left">
                    <span className="font-medium">{s.title}</span>
                    <span className="text-xs text-muted-foreground">{s.size}</span>
                    {s.fullLifecycle && <Badge>Idé → bruksskede</Badge>}
                    {s.perspectives.some((p) => p.coverage !== "klart") ? (
                      <Badge variant="secondary">Lucka</Badge>
                    ) : (
                      <Badge variant="outline">Täckt</Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">{s.summary}</p>
                  <p className="text-xs text-muted-foreground">
                    Skeden: {s.phases.join(" → ")} · {s.docs.length} dokument
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {s.keyFactors.map((f) => (
                      <Badge key={f} variant="outline">
                        {f}
                      </Badge>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {s.perspectives.map((p) => (
                      <div key={p.role} className="rounded-md border p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{p.role}</span>
                          <Badge variant={coverageVariant(p.coverage)}>{p.coverage}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{p.works}</p>
                        {p.gap && (
                          <p className="mt-1 text-sm text-destructive">Lucka: {p.gap}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Dokumenttäckning</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {summary.docsOk} av {summary.docsTotal} dokument förekommer i minst två av projekten.
            {summary.docsThin.length > 0 && ` Tunn täckning: ${summary.docsThin.join(", ")}.`}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {docCoverage.map((d) => (
              <div key={d.docNumber} className="flex items-center justify-between rounded-md border p-2">
                <span className="text-sm">
                  <span className="font-medium">Dok {d.docNumber}</span> · {d.title}
                </span>
                <Badge variant={d.ok ? "outline" : "destructive"}>{d.count} projekt</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Händelsescenarier – vad, var och när</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {EVENTS.map((e) => {
            const scenario = SCENARIOS.find((s) => s.id === e.scenario);
            return (
              <div key={e.id} className="rounded-md border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{scenario?.title}</span>
                  <Badge variant="outline">{e.when}</Badge>
                  <Badge
                    variant={
                      e.outcome === "hanteras"
                        ? "default"
                        : e.outcome === "delvis"
                        ? "secondary"
                        : "destructive"
                    }
                  >
                    {e.outcome}
                  </Badge>
                </div>
                <p className="mt-1 text-sm">{e.what}</p>
                <p className="mt-1 text-sm text-muted-foreground">{e.systemResponse}</p>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Luckor och prioritering</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {GAPS.map((g) => (
            <div key={g.id} className="rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{g.title}</span>
                <Badge variant={g.priority === "hög" ? "destructive" : "secondary"}>
                  Prioritet {g.priority}
                </Badge>
                {g.status === "åtgärdat" ? (
                  <Badge variant="outline" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Åtgärdat
                  </Badge>
                ) : (
                  <Badge variant="outline">Att bygga</Badge>
                )}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{g.description}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Syns i:{" "}
                {g.scenarios
                  .map((id) => SCENARIOS.find((s) => s.id === id)?.title)
                  .filter(Boolean)
                  .join(", ")}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default ScenarioAudit;
